/** Optional schedule prefix in pooja URLs: `/pooja/live/:slug` */
export const POOJA_SCHEDULE_PATH_SEGMENTS = ['live', 'upcoming', 'past'];

const POOJA_SCHEDULE_PATH_SET = new Set(POOJA_SCHEDULE_PATH_SEGMENTS);

export function isPoojaSchedulePathSegment(segment) {
  if (segment == null || segment === '') return false;
  return POOJA_SCHEDULE_PATH_SET.has(String(segment).toLowerCase());
}

/**
 * Parse `/pooja/:slug`, `/pooja/live/:slug`, `/pooja/:slug/book`, `/pooja/:slug/book/pay`, etc.
 * @returns {{ scheduleSegment: string|null, slug: string|null, isBook: boolean, isBookPay: boolean }}
 */
export function parsePoojaPathname(pathname) {
  const empty = { scheduleSegment: null, slug: null, isBook: false, isBookPay: false };
  const p = String(pathname || '').replace(/\/+$/, '');
  if (!p.startsWith('/pooja/')) return empty;

  let m = /^\/pooja\/(live|upcoming|past)\/([^/]+)\/book\/pay$/i.exec(p);
  if (m) {
    return {
      scheduleSegment: m[1].toLowerCase(),
      slug: m[2],
      isBook: true,
      isBookPay: true,
    };
  }

  m = /^\/pooja\/([^/]+)\/book\/pay$/i.exec(p);
  if (m) {
    const first = m[1];
    if (isPoojaSchedulePathSegment(first)) return empty;
    return { scheduleSegment: null, slug: first, isBook: true, isBookPay: true };
  }

  m = /^\/pooja\/(live|upcoming|past)\/([^/]+)\/book$/i.exec(p);
  if (m) {
    return {
      scheduleSegment: m[1].toLowerCase(),
      slug: m[2],
      isBook: true,
      isBookPay: false,
    };
  }

  m = /^\/pooja\/([^/]+)\/book$/i.exec(p);
  if (m) {
    const first = m[1];
    if (isPoojaSchedulePathSegment(first)) return empty;
    return { scheduleSegment: null, slug: first, isBook: true, isBookPay: false };
  }

  m = /^\/pooja\/(live|upcoming|past)\/([^/]+)$/i.exec(p);
  if (m) {
    return {
      scheduleSegment: m[1].toLowerCase(),
      slug: m[2],
      isBook: false,
      isBookPay: false,
    };
  }

  m = /^\/pooja\/([^/]+)$/i.exec(p);
  if (m) {
    const first = m[1];
    if (isPoojaSchedulePathSegment(first)) return empty;
    return { scheduleSegment: null, slug: first, isBook: false, isBookPay: false };
  }

  return empty;
}

export function extractPoojaSlugFromPathname(pathname) {
  const { slug } = parsePoojaPathname(pathname);
  return slug || undefined;
}

export function isPoojaDetailPathname(pathname) {
  const { slug, isBook } = parsePoojaPathname(pathname);
  return Boolean(slug && !isBook);
}

export function isPoojaBookPathname(pathname) {
  const { slug, isBook } = parsePoojaPathname(pathname);
  return Boolean(slug && isBook);
}

export function isPoojaBookPayPathname(pathname) {
  const { slug, isBookPay } = parsePoojaPathname(pathname);
  return Boolean(slug && isBookPay);
}

export function buildPoojaDetailPathname(slug, scheduleSegment = null) {
  if (!slug) return '/';
  const seg =
    scheduleSegment && isPoojaSchedulePathSegment(scheduleSegment)
      ? String(scheduleSegment).toLowerCase()
      : null;
  return seg ? `/pooja/${seg}/${slug}` : `/pooja/${slug}`;
}

export function buildPoojaBookPathname(slug, scheduleSegment = null) {
  return `${buildPoojaDetailPathname(slug, scheduleSegment)}/book`;
}

/** In-place payment URL mask: `/pooja/:slug/book/pay` */
export function buildPoojaBookPayPathname(slug, scheduleSegment = null) {
  return `${buildPoojaBookPathname(slug, scheduleSegment)}/pay`;
}

export function normalizePoojaBookPathname(pathname) {
  const parsed = parsePoojaPathname(pathname);
  if (!parsed.slug || !parsed.isBook) return pathname;
  if (parsed.isBookPay) return buildPoojaBookPayPathname(parsed.slug, parsed.scheduleSegment);
  return buildPoojaBookPathname(parsed.slug, parsed.scheduleSegment);
}
